// Generated with util/create-component.js
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { ColumnsType } from 'antd/lib/table';
import { Table, Button, Modal, Dropdown, Drawer, MenuProps, notification } from 'antd';
import type { NotificationArgsProps } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { saveAs } from 'file-saver';

import DATE_FORMATTER from 'utils/date_formatter';
import PHOTOS from 'utils/photos';
import buildPath from 'utils/build_path';
import formatCurrencyPrice from 'utils/format_currency_price';
import isGiftcardInventorySoldOut from 'utils/is_giftcard_invetory_sold_out';
import routes from 'routing/routes';

import SearchInput from 'components/search_input';
import { IPublishedGiftCardsProps, IPublishedGiftCard } from './published_gift_cards.types';
import styles from './published_gift_cards.module.scss';
import DRAWER_SIZE from '../../../constants/drawer_size';
import GIFT_CARD_DRAWER_SIZE from '../../../constants/gift_card_drawer_size';

import GiftCardIssueForm from '../../gift_card_issue_form/gift_card_issue_form';
import { IGiftCardMultipleIssueFormValues } from '../../gift_card_issue_form/gift_card_issue_form.types';
import { PropertyGiftCardsActionsContext, PropertySettingsDataContext } from '../../../containers/data_context';
import GiftCardDetails from 'components/gift_card_details/gift_card_details';

type NotificationPlacement = NotificationArgsProps['placement'];

const COLUMN_WIDTH = 150;

const openNotification = (placement: NotificationPlacement, downloadStarted: string, downloadDescription: string) => {
  notification.info({
    message: downloadStarted,
    description: downloadDescription,
    placement,
  });
};

const PublishedGiftCards: React.FC<IPublishedGiftCardsProps> = ({
  giftCards,
  property,
  onUpublishGiftCard,
  onLoadRatePlans,
  isLoadingRatePlans,
  selectedProperty,
  groupStores,
  handleDeletePhoto,
  userHotelRoomTypeRatePlans,
  pdfTemplates,
  isLoadingPdfTemplates,
  onLoadPdfTemplates,
  filledSections,
  isLoadingFilledSections,
  onLoadFilledSections,
  onSaveChanges,
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  const propertyGiftCardsActions = useContext(PropertyGiftCardsActionsContext);
  const propertySettingsData = useContext(PropertySettingsDataContext);
  const [isModalVisible, toggleModalVisibility] = useState<boolean>(false);
  const [isDownloadDisabled, setDownloadDisabled] = useState<boolean>(false);
  const [showGiftCardForm, setShowGiftCardForm] = useState<boolean>(false);
  const [showEditGiftCardForm, setShowEditGiftCardForm] = useState<boolean>(false);
  const [giftCard, setGiftCard] = useState<IPublishedGiftCard>();
  const [publishedList, setPublishedList] = useState<IPublishedGiftCard[]>(giftCards);

  useEffect(() => {
    setPublishedList(giftCards);
  }, [giftCards]);

  const renderMenu = (record: IPublishedGiftCard): MenuProps => {
    const menuItems: MenuProps['items'] = [
      { type: 'divider' },
      {
        label: t('gift_card.store_view'),
        key: 'store',
        onClick: () => handleViewInStore(record),
        disabled: isGiftcardInventorySoldOut(record),
      },
      {
        label: t('gift_card.pdf_preview'),
        key: 'pdf',
        onClick: () => handlePdfPreview(record),
        disabled: isDownloadDisabled,
      },
      {
        label: t('gift_card.create_order'),
        key: 'order',
        onClick: () => openDrawer(record),
        disabled: isGiftcardInventorySoldOut(record),
      },
      {
        label: t('gift_card.edit_product'),
        key: 'edit',
        onClick: () => handleEdit(record.id),
      },
      {
        label: 'Duplicate',
        key: 'duplicate',
        onClick: () => handleDuplicate(record.id),
      },
      {
        label: t('general.unpublish'),
        key: 'archive',
        onClick: () => showModal(record),
      },
    ];
    return { items: menuItems };
  };

  const tableColumns: ColumnsType<IPublishedGiftCard> = [
    {
      title: t('general.title'),
      dataIndex: 'title',
      key: 'title',
      width: COLUMN_WIDTH,
    },
    {
      title: t('bookings_list.price_title'),
      dataIndex: 'price',
      key: 'price',
      render: (record: IPublishedGiftCard['price']) => (
        <div>{formatCurrencyPrice(record, property?.currency, property?.country)}</div>
      ),
      width: COLUMN_WIDTH,
    },
    {
      title: t('gift_card.discount_label'),
      dataIndex: 'netPrice',
      key: 'netPrice',
      render: (value, record: IPublishedGiftCard) => (
        <div>
          {value === record.price
            ? t('gift_card.no_discount')
            : formatCurrencyPrice(record.netPrice, property?.currency, property?.country)}
        </div>
      ),
      width: COLUMN_WIDTH,
    },
    {
      title: t('gift_card.validity_label'),
      dataIndex: 'expirationDuration',
      key: 'expirationDuration',
      render: (expirationDuration: IPublishedGiftCard['expirationDuration']) => (
        <div>
          {expirationDuration} {expirationDuration == 1 ? t('general.month') : t('general.months')}
        </div>
      ),
      width: COLUMN_WIDTH,
    },
    {
      title: t('link.inventory'),
      key: 'inventory',
      dataIndex: 'inventory',
      width: COLUMN_WIDTH,
    },
    {
      title: t('general.created_at'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt: IPublishedGiftCard['createdAt']) => <div>{DATE_FORMATTER.toUiDateTime(createdAt)}</div>,
      sorter: (b1: IPublishedGiftCard, b2: IPublishedGiftCard) =>
        dayjs(b1.createdAt).diff(dayjs(b2.createdAt), 'second'),
      defaultSortOrder: 'descend',
      width: COLUMN_WIDTH,
    },
    {
      title: t('general.updated_at'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (updatedAt: IPublishedGiftCard['updatedAt']) => <div>{DATE_FORMATTER.toUiDateTime(updatedAt)}</div>,
      sorter: (b1: IPublishedGiftCard, b2: IPublishedGiftCard) =>
        dayjs(b1.updatedAt).diff(dayjs(b2.updatedAt), 'second'),
      width: COLUMN_WIDTH,
    },
    {
      key: 'actions',
      render: (_, record) => (
        <Dropdown menu={renderMenu(record)}>
          <div className={styles.actions_link}>
            <span className={styles.action_link_text}>{t('general.actions')}</span> <DownOutlined />
          </div>
        </Dropdown>
      ),
      width: COLUMN_WIDTH,
    },
  ];

  const showModal = (giftCard: IPublishedGiftCard) => {
    toggleModalVisibility(true);
    setGiftCard(giftCard);
  };

  const openDrawer = (giftCard: IPublishedGiftCard) => {
    setShowGiftCardForm(true);
    setGiftCard(giftCard);
  };

  const closeDrawer = () => {
    setShowGiftCardForm(false);
  };

  const openEditDrawer = () => {
    setShowEditGiftCardForm(true);
    onLoadRatePlans();
  };

  const handleEdit = (giftCardId: IPublishedGiftCard['id']) => {
    const giftCard = publishedList.find(giftCard => giftCard.id == giftCardId);
    setGiftCard(giftCard);
    openEditDrawer();
  };

  const handleDuplicate = async (giftCardId: IPublishedGiftCard['id']) => {
    const giftCard = publishedList.find(giftCard => giftCard.id == giftCardId);
    if (giftCard) {
      const duplicatedPhotos = await PHOTOS.duplicateGiftCardPhotos(giftCard.photos);
      giftCard.photos = duplicatedPhotos;

      setGiftCard({ ...giftCard, title: `Copy of ${giftCard.title}`, isDuplicate: true });
      openEditDrawer();
    }
  };

  const handleViewInStore = (giftCard: IPublishedGiftCard) => {
    const slugName = property?.activeSlug;
    const path = buildPath('', routes.giftCardStoreDetailPage, { selectedProperty: slugName, giftCardId: giftCard.id });
    history.push(path);
  };

  const handlePdfPreview = async (giftCard: IPublishedGiftCard) => {
    try {
      setDownloadDisabled(true);
      openNotification('bottomRight', t('gift_card.download_started'), t('gift_card.download_description'));
      const pdf = await propertyGiftCardsActions.getGiftCardDetailsPdfPreview({
        giftCardTypeId: giftCard.id,
        propertyId: selectedProperty,
      });
      saveAs(pdf, `gift_card_${giftCard?.title}_preview`);
    } catch (e) {
      openNotification('topRight', t('gift_card.download_failed'), t('gift_card.download_failed_message'));
      console.log('error', e);
    } finally {
      setDownloadDisabled(false);
    }
  };

  const closeEditDrawer = () => {
    setShowEditGiftCardForm(false);
  };

  const handleUnpublish = () => {
    toggleModalVisibility(false);
    if (giftCard) {
      onUpublishGiftCard(giftCard.id);
    }
  };

  const handleIssuingMultipleGiftCard = async (
    giftCardId: IPublishedGiftCard['id'],
    issuedParams: IGiftCardMultipleIssueFormValues,
  ) => {
    await propertyGiftCardsActions.postGiftCardIssuedMultiple({
      propertyId: propertySettingsData.selectedProperty,
      giftCardId,
      issuedParams,
    });
  };

  const handleCancel = () => {
    toggleModalVisibility(false);
  };

  const handleSearchTermChange = useCallback(
    (term: string) => {
      const publishedFiltered = giftCards?.filter((giftCard: IPublishedGiftCard) =>
        giftCard.title.toLowerCase().includes(term.toLowerCase()),
      );
      setPublishedList(publishedFiltered);
    },
    [giftCards],
  );

  return (
    <div data-testid="PublishedGiftCards" className={styles.root}>
      <SearchInput
        onSearch={handleSearchTermChange}
        placeholder={t('general.search_for_a_title')}
        className={styles.search_input}
      />

      <Table
        dataSource={publishedList}
        columns={tableColumns}
        rowKey="id"
        pagination={{ defaultPageSize: 10, hideOnSinglePage: true }}
        data-testid="PublishedGiftCardsList"
        scroll={{ x: 'max-content', y: window.innerHeight - (window.innerWidth < 900 ? 400 : 340) }}
      />
      <Modal
        open={isModalVisible}
        title={t('general.confirm')}
        onOk={handleUnpublish}
        onCancel={handleCancel}
        footer={[
          <Button key="back" onClick={handleCancel}>
            {t('link.cancel')}
          </Button>,
          <Button key="submit" type="primary" onClick={handleUnpublish}>
            {t('general.unpublish')}
          </Button>,
        ]}
      >
        <p>{`Are you sure you want to unpublish ${giftCard?.title} ?`}</p>
        <p>This will remove the gift card from the store.</p>
      </Modal>

      <Drawer
        destroyOnClose
        title={t('gift_card.create_order')}
        open={showGiftCardForm}
        onClose={closeDrawer}
        width={window.innerWidth > 900 ? DRAWER_SIZE.LARGE : window.innerWidth}
      >
        {giftCard && (
          <GiftCardIssueForm
            giftCardType={giftCard}
            onCancel={closeDrawer}
            onSaveChanges={handleIssuingMultipleGiftCard}
            currency={property?.currency}
          />
        )}
      </Drawer>

      <Drawer
        destroyOnClose
        title={giftCard?.isDuplicate ? 'Create a new product' : t('gift_card.edit')}
        open={showEditGiftCardForm}
        onClose={closeEditDrawer}
        width={window.innerWidth > 900 ? GIFT_CARD_DRAWER_SIZE.LARGE : window.innerWidth}
      >
        {giftCard && (
          <GiftCardDetails
            giftCard={giftCard}
            isLoadingRatePlans={isLoadingRatePlans}
            onSaveChanges={onSaveChanges}
            currency={property?.currency}
            selectedProperty={selectedProperty}
            onDeletePhoto={handleDeletePhoto}
            userHotelRoomTypeRatePlans={userHotelRoomTypeRatePlans}
            groupStores={groupStores}
            pdfTemplates={pdfTemplates}
            onLoadPdfTemplates={onLoadPdfTemplates}
            isLoadingPdfTemplates={isLoadingPdfTemplates}
            filledSections={filledSections}
            isLoadingFilledSections={isLoadingFilledSections}
            onLoadFilledSections={onLoadFilledSections}
          />
        )}
      </Drawer>
    </div>
  );
};

export default PublishedGiftCards;
